/**
 * Customer Form Page
 * Create (/customers/new) and edit (/customers/:id/edit) a customer.
 *
 * Only ADMIN and OFFICE users may create or update customers (the backend
 * enforces this with requireRole); other roles see a permission notice.
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { createAPIClient, ICustomersApi, IDispatchApi } from '../api';
import * as types from '../api/types';
import { getErrorMessage } from '../utils/apiError';
import { canManageCustomers } from '../utils/permissions';
import { CUSTOMER_STATUS_LABELS, CUSTOMER_STATUS_OPTIONS } from '../components/CustomerStatusBadge';
import { LeadSourceSelect } from '../components/LeadSourceSelect';
import { Toast } from '../components/Toast';
import { RoutePropagationDialog, RouteSelect, useDispatchRoutes } from '../components/RouteControls';
import { routeName } from './routeUi';

// Same rule as backend customer.business.ts
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const PHONE_DIGITS = 10;

/** Digits only, dropping a leading US country code and capping at 10 digits. */
function phoneDigits(value: string): string {
  let digits = value.replace(/\D/g, '');
  if (digits.length > PHONE_DIGITS && digits.startsWith('1')) digits = digits.slice(1);
  return digits.slice(0, PHONE_DIGITS);
}

/**
 * Progressively formats input as a US phone number:
 * "5" -> "(5", "5551" -> "(555) 1", "5551234567" -> "(555) 123-4567".
 */
function formatPhone(value: string): string {
  const digits = phoneDigits(value);
  if (digits.length === 0) return '';
  if (digits.length <= 3) return `(${digits}`;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

/**
 * Stored phones that are a complete US number are shown formatted; anything
 * else (legacy/international values) is shown as-is until the user edits it.
 */
function phoneForDisplay(stored: string | null | undefined): string {
  if (!stored) return '';
  const raw = stored.replace(/\D/g, '');
  const isUsNumber = raw.length === PHONE_DIGITS || (raw.length === PHONE_DIGITS + 1 && raw.startsWith('1'));
  return isUsNumber ? formatPhone(stored) : stored;
}

interface FormState {
  name: string;
  email: string;
  phone: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  taxExempt: boolean;
  billingMethod: types.BillingMethod;
  status: types.CustomerStatus;
  leadSourceId: string | null;
  /** Exclude from auto-invoicing (tracker item 37) */
  autoInvoiceExempt: boolean;
  /** Default dispatch route; '' = none (tracker item 57) */
  routeId: string;
}

const EMPTY_FORM: FormState = {
  name: '',
  email: '',
  phone: '',
  address: '',
  city: '',
  state: '',
  zip: '',
  taxExempt: false,
  billingMethod: 'PER_VISIT',
  status: 'LEAD',
  leadSourceId: null,
  autoInvoiceExempt: false,
  routeId: '',
};

type OptionalTextField = 'email' | 'phone' | 'address' | 'city' | 'state' | 'zip';
const OPTIONAL_TEXT_FIELDS: OptionalTextField[] = ['email', 'phone', 'address', 'city', 'state', 'zip'];

function toFormState(customer: types.Customer): FormState {
  return {
    name: customer.name,
    email: customer.email ?? '',
    phone: phoneForDisplay(customer.phone),
    address: customer.address ?? '',
    city: customer.city ?? '',
    state: customer.state ?? '',
    zip: customer.zip ?? '',
    taxExempt: customer.taxExempt,
    billingMethod: customer.billingMethod,
    status: customer.status,
    leadSourceId: customer.leadSourceId ?? null,
    autoInvoiceExempt: customer.autoInvoiceExempt ?? false,
    routeId: customer.routeId ?? '',
  };
}

function buildCreateRequest(form: FormState): types.CreateCustomerRequest {
  const request: types.CreateCustomerRequest = {
    name: form.name.trim(),
    taxExempt: form.taxExempt,
    billingMethod: form.billingMethod,
    status: form.status,
    autoInvoiceExempt: form.autoInvoiceExempt,
  };
  if (form.leadSourceId) request.leadSourceId = form.leadSourceId;
  if (form.routeId) request.routeId = form.routeId;
  for (const field of OPTIONAL_TEXT_FIELDS) {
    const value = form[field].trim();
    if (value) request[field] = value;
  }
  return request;
}

/**
 * Explicit whitelist: the backend PATCH passes the body straight to Prisma,
 * so never send server-owned fields (id, createdAt, summary, ...).
 * Cleared optional fields are sent as null so the stored value is removed.
 * leadSourceId is always sent; the backend records lead source history only
 * when it differs from the saved value. routeId is never sent here: an
 * existing customer's default route changes through setRouteDefault, which
 * also decides what happens to existing jobs.
 */
function buildUpdateRequest(form: FormState): types.UpdateCustomerRequest {
  const request: types.UpdateCustomerRequest = {
    name: form.name.trim(),
    taxExempt: form.taxExempt,
    billingMethod: form.billingMethod,
    status: form.status,
    leadSourceId: form.leadSourceId,
    autoInvoiceExempt: form.autoInvoiceExempt,
  };
  for (const field of OPTIONAL_TEXT_FIELDS) {
    const value = form[field].trim();
    request[field] = value ? value : null;
  }
  return request;
}

function validate(form: FormState): string | null {
  if (!form.name.trim()) return 'Name is required';
  const email = form.email.trim();
  if (email && !EMAIL_PATTERN.test(email)) return 'Invalid email format';
  // Only check numbers entered through the mask; untouched legacy values pass through.
  const phone = form.phone.trim();
  if (phone.startsWith('(') && phoneDigits(phone).length < PHONE_DIGITS) {
    return 'Enter a complete 10-digit phone number';
  }
  return null;
}

const inputClass =
  'block w-full rounded-lg border border-gray-300 bg-white px-3.5 py-2.5 text-gray-900 shadow-sm ' +
  'placeholder:text-gray-400 transition duration-150 ' +
  'hover:border-gray-400 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 ' +
  'disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-500';
const labelClass = 'block text-sm font-medium text-gray-700 mb-1.5';

interface TextFieldProps {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  disabled: boolean;
  type?: string;
  required?: boolean;
  optional?: boolean;
  placeholder?: string;
  autoComplete?: string;
  inputMode?: React.HTMLAttributes<HTMLInputElement>['inputMode'];
  maxLength?: number;
  hint?: string;
  className?: string;
}

function TextField({
  id,
  label,
  value,
  onChange,
  disabled,
  type = 'text',
  required,
  optional,
  placeholder,
  autoComplete,
  inputMode,
  maxLength,
  hint,
  className,
}: TextFieldProps) {
  return (
    <div className={className}>
      <label htmlFor={id} className={labelClass}>
        {label}
        {required && <span className="ml-0.5 text-red-600">*</span>}
        {optional && <span className="ml-1.5 text-xs font-normal text-gray-400">Optional</span>}
      </label>
      <input
        id={id}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        required={required}
        placeholder={placeholder}
        autoComplete={autoComplete}
        inputMode={inputMode}
        maxLength={maxLength}
        aria-describedby={hint ? `${id}-hint` : undefined}
        className={inputClass}
      />
      {hint && (
        <p id={`${id}-hint`} className="mt-1.5 text-xs text-gray-500">
          {hint}
        </p>
      )}
    </div>
  );
}

interface FormSectionProps {
  title: string;
  description?: string;
  children: React.ReactNode;
}

function FormSection({ title, description, children }: FormSectionProps) {
  return (
    <div className="grid grid-cols-1 gap-x-8 gap-y-4 py-6 first:pt-0 last:pb-0 md:grid-cols-3">
      <div>
        <h2 className="text-base font-semibold text-gray-900">{title}</h2>
        {description && <p className="mt-1 text-sm text-gray-500">{description}</p>}
      </div>
      <div className="md:col-span-2">{children}</div>
    </div>
  );
}

export function CustomerFormPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);
  const { user } = useAuth();
  const apiClient = React.useMemo<ICustomersApi & IDispatchApi>(() => createAPIClient(), []);
  const canManage = canManageCustomers(user);
  const { routes, error: routesError } = useDispatchRoutes(apiClient, canManage);

  // State
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [savedLeadSourceId, setSavedLeadSourceId] = useState<string | null>(null);
  const [savedRouteId, setSavedRouteId] = useState<string | null>(null);
  const [routePrompt, setRoutePrompt] = useState(false);
  const [isLoading, setIsLoading] = useState(isEdit);
  const [isSaving, setIsSaving] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Load existing customer in edit mode
  useEffect(() => {
    if (!id || !canManage) {
      setIsLoading(false);
      return;
    }

    const loadCustomer = async () => {
      try {
        setIsLoading(true);
        setLoadError(null);

        const customer = await apiClient.getCustomer(id);
        setForm(toFormState(customer));
        setSavedLeadSourceId(customer.leadSourceId ?? null);
        setSavedRouteId(customer.routeId ?? null);
      } catch (err) {
        setLoadError(getErrorMessage(err, 'Failed to load customer'));
      } finally {
        setIsLoading(false);
      }
    };

    loadCustomer();
  }, [apiClient, id, canManage]);

  const cancelTarget = id ? `/customers/${id}` : '/customers';

  const setField = <K extends keyof FormState>(field: K, value: FormState[K]) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const routeChanged = isEdit && (form.routeId || null) !== savedRouteId;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const validationError = validate(form);
    if (validationError) {
      setError(validationError);
      return;
    }
    // A changed default on an existing customer asks what happens to its jobs first.
    if (routeChanged) {
      setError(null);
      setRoutePrompt(true);
      return;
    }
    await save();
  };

  const save = async (routePropagation?: types.RoutePropagation) => {
    try {
      setIsSaving(true);
      setError(null);

      const saved = id
        ? await apiClient.updateCustomer(id, buildUpdateRequest(form))
        : await apiClient.createCustomer(buildCreateRequest(form));

      if (id && routePropagation) {
        try {
          await apiClient.setRouteDefault('customer', id, { routeId: form.routeId || null, routePropagation });
        } catch (err) {
          setRoutePrompt(false);
          setError(`The other changes were saved, but the default route was not changed: ${getErrorMessage(err, 'Could not change the default route')}`);
          return;
        }
      }

      navigate(`/customers/${saved.id}`, { replace: true });
    } catch (err) {
      setRoutePrompt(false);
      setError(getErrorMessage(err, isEdit ? 'Failed to update customer' : 'Failed to create customer'));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
      {/* Title */}
      <div className="mb-6">
        <button
          onClick={() => navigate(cancelTarget)}
          className="inline-flex items-center min-h-[44px] sm:min-h-0 text-blue-600 hover:text-blue-700 text-sm font-semibold"
        >
          ← {isEdit ? 'Customer' : 'Customers'}
        </button>
        <h1 className="text-2xl font-bold text-gray-900 mt-1">
          {isEdit ? 'Edit Customer' : 'New Customer'}
        </h1>
      </div>

      {!canManage ? (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md">
          <p className="text-red-800">
            You do not have permission to {isEdit ? 'edit' : 'create'} customers.
          </p>
        </div>
      ) : isLoading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
        </div>
      ) : loadError ? (
        <>
          <Toast kind="error">{loadError}</Toast>
          <p className="mb-6 text-gray-700">The customer could not be loaded.</p>
        </>
      ) : (
        <section className="bg-white rounded-lg shadow max-w-4xl">
          <form onSubmit={handleSubmit} noValidate>
            <div className="p-4 sm:p-8">
              {error && <Toast kind="error">{error}</Toast>}

              <div className="divide-y divide-gray-200">
                {/* Contact */}
                <FormSection title="Contact" description="Who this customer is and how to reach them.">
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <TextField
                      id="name"
                      label="Name"
                      value={form.name}
                      onChange={(v) => setField('name', v)}
                      disabled={isSaving}
                      required
                      autoComplete="organization"
                      placeholder="Acme Corp"
                      className="sm:col-span-2"
                    />
                    <TextField
                      id="email"
                      type="email"
                      label="Email"
                      value={form.email}
                      onChange={(v) => setField('email', v)}
                      disabled={isSaving}
                      optional
                      autoComplete="email"
                      placeholder="billing@example.com"
                    />
                    <TextField
                      id="phone"
                      type="tel"
                      label="Phone"
                      value={form.phone}
                      onChange={(v) => setField('phone', formatPhone(v))}
                      disabled={isSaving}
                      optional
                      autoComplete="tel-national"
                      inputMode="tel"
                      placeholder="(555) 123-4567"
                    />
                  </div>
                </FormSection>

                {/* Address */}
                <FormSection title="Address" description="Mailing address used on invoices.">
                  <fieldset className="rounded-lg border border-gray-200 bg-gray-50 p-4 sm:p-5">
                    <legend className="sr-only">Address</legend>
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-6">
                      <TextField
                        id="address"
                        label="Street address"
                        value={form.address}
                        onChange={(v) => setField('address', v)}
                        disabled={isSaving}
                        autoComplete="street-address"
                        placeholder="123 Main St"
                        className="sm:col-span-6"
                      />
                      <TextField
                        id="city"
                        label="City"
                        value={form.city}
                        onChange={(v) => setField('city', v)}
                        disabled={isSaving}
                        autoComplete="address-level2"
                        className="sm:col-span-3"
                      />
                      <TextField
                        id="state"
                        label="State"
                        value={form.state}
                        onChange={(v) => setField('state', v)}
                        disabled={isSaving}
                        autoComplete="address-level1"
                        className="sm:col-span-1"
                      />
                      <TextField
                        id="zip"
                        label="ZIP"
                        value={form.zip}
                        onChange={(v) => setField('zip', v)}
                        disabled={isSaving}
                        autoComplete="postal-code"
                        inputMode="numeric"
                        className="sm:col-span-2"
                      />
                    </div>
                  </fieldset>
                </FormSection>

                {/* Status */}
                <FormSection title="Status" description="Where this customer is in your sales process.">
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div>
                      <label htmlFor="status" className={labelClass}>Customer status</label>
                      <select
                        id="status"
                        value={form.status}
                        onChange={(e) => setField('status', e.target.value as types.CustomerStatus)}
                        disabled={isSaving}
                        className={inputClass}
                      >
                        {CUSTOMER_STATUS_OPTIONS.map((status) => (
                          <option key={status} value={status}>
                            {CUSTOMER_STATUS_LABELS[status]}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </FormSection>

                {/* Lead Source */}
                <FormSection
                  title="Lead Source"
                  description="How this customer found you. Changes are kept in the customer's lead source history."
                >
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div className="sm:col-span-2">
                      <label htmlFor="leadSourceId" className={labelClass}>
                        Lead source
                        <span className="ml-1.5 text-xs font-normal text-gray-400">Optional</span>
                      </label>
                      <LeadSourceSelect
                        id="leadSourceId"
                        value={form.leadSourceId}
                        onChange={(v) => setField('leadSourceId', v)}
                        currentId={savedLeadSourceId}
                        disabled={isSaving}
                      />
                    </div>
                  </div>
                </FormSection>

                {/* Billing */}
                <FormSection title="Billing" description="How work for this customer is invoiced and taxed.">
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div>
                      <label htmlFor="billingMethod" className={labelClass}>Billing method</label>
                      <select
                        id="billingMethod"
                        value={form.billingMethod}
                        onChange={(e) => setField('billingMethod', e.target.value as types.BillingMethod)}
                        disabled={isSaving}
                        className={inputClass}
                      >
                        <option value="PER_VISIT">Per Visit</option>
                        <option value="CONSOLIDATED">Consolidated</option>
                      </select>
                    </div>

                    <label
                      htmlFor="taxExempt"
                      className="flex cursor-pointer items-start gap-3 rounded-lg border border-gray-200 p-3.5 transition duration-150 hover:bg-gray-50 sm:mt-7"
                    >
                      <input
                        id="taxExempt"
                        type="checkbox"
                        checked={form.taxExempt}
                        onChange={(e) => setField('taxExempt', e.target.checked)}
                        disabled={isSaving}
                        className="mt-0.5 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span>
                        <span className="block text-sm font-medium text-gray-900">Tax exempt</span>
                        <span className="block text-xs text-gray-500">No sales tax is applied to this customer's invoices.</span>
                      </span>
                    </label>

                    <label
                      htmlFor="autoInvoiceExempt"
                      className="flex cursor-pointer items-start gap-3 rounded-lg border border-gray-200 p-3.5 transition duration-150 hover:bg-gray-50 sm:col-span-2"
                    >
                      <input
                        id="autoInvoiceExempt"
                        type="checkbox"
                        checked={form.autoInvoiceExempt}
                        onChange={(e) => setField('autoInvoiceExempt', e.target.checked)}
                        disabled={isSaving}
                        className="mt-0.5 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span>
                        <span className="block text-sm font-medium text-gray-900">Exclude from auto-invoicing</span>
                        <span className="block text-xs text-gray-500">
                          Completed jobs billed to this customer are never invoiced automatically; invoice them by hand.
                        </span>
                      </span>
                    </label>
                  </div>
                </FormSection>

                {/* Dispatch route (tracker item 57) */}
                <FormSection title="Dispatch" description="Default route for this customer's new jobs. A property's own default route wins.">
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <RouteSelect
                      id="routeId"
                      label="Default route"
                      value={form.routeId}
                      onChange={(v) => setField('routeId', v)}
                      routes={routes}
                      currentId={savedRouteId}
                      leadingOptions={[{ value: '', label: 'No default route' }]}
                      disabled={isSaving}
                      loadError={routesError}
                      hint={isEdit ? 'Saving a different route asks what to do with existing jobs.' : undefined}
                    />
                  </div>
                </FormSection>
              </div>
            </div>

            {/* Actions */}
            <div className="flex flex-col-reverse gap-3 rounded-b-lg border-t border-gray-200 bg-gray-50 px-4 py-4 sm:flex-row sm:justify-end sm:px-8">
              <button
                type="button"
                onClick={() => navigate(cancelTarget)}
                disabled={isSaving}
                className="w-full sm:w-auto py-2.5 sm:py-2 px-4 rounded-md border border-gray-300 bg-white font-semibold text-gray-700 hover:bg-gray-50 transition duration-200"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSaving}
                className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white font-semibold w-full sm:w-auto py-2.5 sm:py-2 px-4 rounded-md transition duration-200"
              >
                {isSaving ? 'Saving...' : isEdit ? 'Save Changes' : 'Create Customer'}
              </button>
            </div>
          </form>
          {routePrompt && (
            <RoutePropagationDialog
              entity="customer"
              oldName={savedRouteId ? routeName(routes, savedRouteId) : null}
              newName={form.routeId ? routeName(routes, form.routeId) : null}
              busy={isSaving}
              onConfirm={(choice) => void save(choice)}
              onCancel={() => setRoutePrompt(false)}
            />
          )}
        </section>
      )}
    </main>
  );
}
